import React from 'react';
import { Outlet, Route } from 'react-router-dom';
import LazyComponent from '@/components/LazyComponent';
import { AiCoursewareHeaderProvider } from '@/pages/teacher/ai-courseware/components/AiCoursewareHeaderContext';

function AiCoursewareShell() {
  return (
    <AiCoursewareHeaderProvider>
      <Outlet />
    </AiCoursewareHeaderProvider>
  );
}

const HomePage = React.lazy(() => import('@/pages/teacher/Home'));
const CoursesPage = React.lazy(() => import('@/pages/teacher/Courses'));
const NewCoursesPage = React.lazy(() => import('@/pages/teacher/NewCourses'));
const NewCoursesOverviewPage = React.lazy(
  () => import('@/pages/teacher/NewCourses/Overview')
);
const NewCoursesModulesPage = React.lazy(
  () => import('@/pages/teacher/NewCourses/Modules')
);
const NewCoursesAssignmentsPage = React.lazy(
  () => import('@/pages/teacher/NewCourses/Assignments')
);
const NewCoursesGradesPage = React.lazy(
  () => import('@/pages/teacher/NewCourses/Grades')
);
const NewCoursesMembersPage = React.lazy(
  () => import('@/pages/teacher/NewCourses/Members')
);
const NewCoursesAnnouncementsPage = React.lazy(
  () => import('@/pages/teacher/NewCourses/Announcements')
);
const CourseDetailPage = React.lazy(
  () => import('@/pages/teacher/CourseDetail')
);
const SubmissionsPage = React.lazy(() => import('@/pages/teacher/Submissions'));
const AssignmentPage = React.lazy(() => import('@/pages/teacher/Assignment'));
const AssignmentDetailPage = React.lazy(
  () => import('@/pages/teacher/AssignmentDetail')
);
const UnitPage = React.lazy(() => import('@/pages/teacher/Unit'));
const UnitDetailPage = React.lazy(() => import('@/pages/teacher/UnitDetail'));
const AnnouncementDetailPage = React.lazy(
  () => import('@/pages/teacher/AnnouncementDetail')
);
const AnnouncementPage = React.lazy(
  () => import('@/pages/teacher/Announcement')
);
const AICoursewarePage = React.lazy(
  () => import('@/pages/teacher/ai-courseware')
);
const AICoursewareCreatePage = React.lazy(
  () => import('@/pages/teacher/ai-courseware/plan/create')
);
const AICoursewarePlanDetailPage = React.lazy(
  () => import('@/pages/teacher/ai-courseware/plan/detail')
);
const AICoursewarePlanEditPage = React.lazy(
  () => import('@/pages/teacher/ai-courseware/plan/edit')
);
const AICoursewarePlanPreviewPage = React.lazy(
  () => import('@/pages/teacher/ai-courseware/plan/preview')
);
const AICoursewarePlanIndexPage = React.lazy(
  () => import('@/pages/teacher/ai-courseware/plan')
);
const AICoursewarePreparePage = React.lazy(
  () => import('@/pages/teacher/ai-courseware/prepare')
);
const AICoursewarePrepareDetailPage = React.lazy(
  () => import('@/pages/teacher/ai-courseware/prepare/detail')
);
const AICoursewarePrepareCreatePage = React.lazy(
  () => import('@/pages/teacher/ai-courseware/prepare/create')
);
const AICoursewarePrepareEditPage = React.lazy(
  () => import('@/pages/teacher/ai-courseware/prepare/edit')
);
const AICoursewarePreparePreviewPage = React.lazy(
  () => import('@/pages/teacher/ai-courseware/prepare/preview')
);
const AICoursewareTeachPage = React.lazy(
  () => import('@/pages/teacher/ai-courseware/teach')
);
const AICoursewareTeachDetailPage = React.lazy(
  () => import('@/pages/teacher/ai-courseware/teach/detail')
);
const AICoursewareTeachEditPage = React.lazy(
  () => import('@/pages/teacher/ai-courseware/teach/edit')
);
const AICoursewareTeachAddFeedbackPage = React.lazy(
  () => import('@/pages/teacher/ai-courseware/teach/add-feedback')
);
const AICoursewareReflectPage = React.lazy(
  () => import('@/pages/teacher/ai-courseware/reflect')
);
const AICoursewareReflectDetailPage = React.lazy(
  () => import('@/pages/teacher/ai-courseware/reflect/detail')
);
const AICoursewareImprovePage = React.lazy(
  () => import('@/pages/teacher/ai-courseware/improve')
);
const MessagesPages = React.lazy(() => import('@/pages/teacher/Messages'));
const SettingsPage = React.lazy(() => import('@/pages/teacher/Settings'));

function Index() {
  return (
    <Route path='teacher' element={<Outlet />}>
      <Route index element={<LazyComponent AsyncComponent={HomePage} />} />
      <Route path='new-courses' element={<Outlet />}>
        <Route
          index
          element={<LazyComponent AsyncComponent={NewCoursesPage} />}
        />
        <Route
          path='Overview'
          element={<LazyComponent AsyncComponent={NewCoursesOverviewPage} />}
        />
        <Route
          path='Modules'
          element={<LazyComponent AsyncComponent={NewCoursesModulesPage} />}
        />
        <Route
          path='Assignments'
          element={<LazyComponent AsyncComponent={NewCoursesAssignmentsPage} />}
        />
        <Route
          path='Grades'
          element={<LazyComponent AsyncComponent={NewCoursesGradesPage} />}
        />
        <Route
          path='Members'
          element={<LazyComponent AsyncComponent={NewCoursesMembersPage} />}
        />
        <Route
          path='Announcements'
          element={
            <LazyComponent AsyncComponent={NewCoursesAnnouncementsPage} />
          }
        />
      </Route>
      <Route path='courses' element={<Outlet />}>
        <Route index element={<LazyComponent AsyncComponent={CoursesPage} />} />
        <Route
          path=':courseId'
          element={<LazyComponent AsyncComponent={CourseDetailPage} />}
        />
        <Route
          path=':courseId/announcements/:announcementId'
          element={<LazyComponent AsyncComponent={AnnouncementDetailPage} />}
        />
        <Route
          path=':courseId/announcement/:announcementId?'
          element={<LazyComponent AsyncComponent={AnnouncementPage} />}
        />
        <Route
          path=':courseId/submissions/:assignmentId'
          element={<LazyComponent AsyncComponent={SubmissionsPage} />}
        />
        <Route
          path=':courseId/assignment/:assignmentId?'
          element={<LazyComponent AsyncComponent={AssignmentPage} />}
        />
        <Route
          path=':courseId/assignments/:assignmentId'
          element={<LazyComponent AsyncComponent={AssignmentDetailPage} />}
        />
        <Route
          path=':courseId/unit/:unitId?'
          element={<LazyComponent AsyncComponent={UnitPage} />}
        />
        <Route
          path=':courseId/units/:unitId'
          element={<LazyComponent AsyncComponent={UnitDetailPage} />}
        />
      </Route>
      <Route path='ai-courseware' element={<AiCoursewareShell />}>
        <Route
          index
          element={<LazyComponent AsyncComponent={AICoursewarePage} />}
        />
        <Route path='plan' element={<Outlet />}>
          <Route
            index
            element={
              <LazyComponent AsyncComponent={AICoursewarePlanIndexPage} />
            }
          />
          <Route
            path='create'
            element={<LazyComponent AsyncComponent={AICoursewareCreatePage} />}
          />
          <Route
            path='detail'
            element={
              <LazyComponent AsyncComponent={AICoursewarePlanDetailPage} />
            }
          />
          <Route
            path='edit'
            element={
              <LazyComponent AsyncComponent={AICoursewarePlanEditPage} />
            }
          />
          <Route
            path='preview'
            element={
              <LazyComponent AsyncComponent={AICoursewarePlanPreviewPage} />
            }
          />
        </Route>
        <Route path='prepare' element={<Outlet />}>
          <Route
            index
            element={<LazyComponent AsyncComponent={AICoursewarePreparePage} />}
          />
          <Route
            path='detail'
            element={
              <LazyComponent AsyncComponent={AICoursewarePrepareDetailPage} />
            }
          />
          <Route
            path='create'
            element={
              <LazyComponent AsyncComponent={AICoursewarePrepareCreatePage} />
            }
          />
          <Route
            path='edit'
            element={
              <LazyComponent AsyncComponent={AICoursewarePrepareEditPage} />
            }
          />
          <Route
            path='preview'
            element={
              <LazyComponent AsyncComponent={AICoursewarePreparePreviewPage} />
            }
          />
        </Route>
        <Route path='teach' element={<Outlet />}>
          <Route
            index
            element={<LazyComponent AsyncComponent={AICoursewareTeachPage} />}
          />
          <Route
            path='detail'
            element={
              <LazyComponent AsyncComponent={AICoursewareTeachDetailPage} />
            }
          />
          <Route
            path='edit'
            element={
              <LazyComponent AsyncComponent={AICoursewareTeachEditPage} />
            }
          />
          <Route
            path='add-feedback'
            element={
              <LazyComponent
                AsyncComponent={AICoursewareTeachAddFeedbackPage}
              />
            }
          />
        </Route>
        <Route path='reflect' element={<Outlet />}>
          <Route
            index
            element={<LazyComponent AsyncComponent={AICoursewareReflectPage} />}
          />
          <Route
            path='detail'
            element={
              <LazyComponent AsyncComponent={AICoursewareReflectDetailPage} />
            }
          />
        </Route>
        <Route
          path='improve'
          element={<LazyComponent AsyncComponent={AICoursewareImprovePage} />}
        />
      </Route>
      <Route
        path='messages'
        element={<LazyComponent AsyncComponent={MessagesPages} />}
      />
      <Route
        path='settings'
        element={<LazyComponent AsyncComponent={SettingsPage} />}
      />
    </Route>
  );
}

export default Index;
